import React from 'react';
import styled from 'styled-components';
import { useLanguage } from '../contexts/LanguageContext';
import { content } from '../data/content';

const FooterContainer = styled.footer`
  background: #000000;
  background-image: 
    repeating-linear-gradient(
      90deg,
      transparent,
      transparent 100px,
      rgba(138, 138, 126, 0.05) 100px,
      rgba(138, 138, 126, 0.05) 101px
    );
  color: #ffffff;
  padding: 4rem 0 2rem;
  position: relative;
  overflow: hidden;
`;

const FooterContent = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 20px;
  position: relative;
  z-index: 3;
`;

const FooterTop = styled.div`
  display: grid;
  grid-template-columns: auto 2fr 1fr 1.5fr;
  gap: 4rem;
  margin-bottom: 3rem;

  @media (max-width: 968px) {
    grid-template-columns: 1fr 1fr;
    gap: 2rem;
  }

  @media (max-width: 568px) {
    grid-template-columns: 1fr;
  }
`;

const FooterSection = styled.div`
  h4 {
    font-size: 1.2rem;
    font-weight: 700;
    margin-bottom: 1.5rem;
    color: #b7ab9f;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

  p, li {
    font-size: 0.95rem;
    line-height: 1.6;
    margin-bottom: 0.8rem;
    opacity: 0.9;
  }

  ul {
    list-style: none;
    padding: 0;
  }

  a {
    color: #ffffff;
    text-decoration: none;
    transition: color 0.3s ease;

    &:hover {
      color: #b7ab9f;
    }
  }
`;

const CompanyInfo = styled.div`
  h3 {
    font-size: 2rem;
    font-weight: 700;
    margin-bottom: 1rem;
    color: #b7ab9f;
  }

  p {
    font-size: 1rem;
    line-height: 1.7;
    margin-bottom: 1rem;
    opacity: 0.9;
  }
`;

const FooterLogo = styled.div`
  display: flex;
  justify-content: center;
  align-items: flex-start;
  
  img {
    height: 200px;
    width: auto;
    max-width: 100%;
    filter: brightness(0.8);
    transition: filter 0.3s ease;
    margin-top: -20px;
  }

  &:hover img {
    filter: brightness(1);
  }

  @media (max-width: 968px) {
    grid-column: 1 / -1;
    justify-content: center;
    margin-bottom: 2rem;
    
    img {
      height: 150px;
    }
  }

  @media (max-width: 568px) {
    img {
      height: 120px;
      margin-top: -10px;
    }
  }

  @media (max-width: 400px) {
    img {
      height: 100px;
      margin-top: 0;
    }
  }
`;

const FooterBottom = styled.div`
  border-top: 1px solid rgba(138, 138, 126, 0.3);
  padding-top: 2rem;
  text-align: center;
  font-size: 0.9rem;
  opacity: 0.8;
`;

const Footer = () => {
  const { language } = useLanguage();
  const t = content[language];

  const currentYear = new Date().getFullYear();

  return (
    <FooterContainer>
      <FooterContent>
        <FooterTop>
          <FooterLogo>
            <img
              src="/images/LogoVertical.jpg"
              alt={language === 'fr' ? 'Groupe Garant' : 'Garant Group'}
            />
          </FooterLogo>

          <CompanyInfo>
            <h3>{language === 'fr' ? 'Groupe Garant' : 'Garant Group'}</h3>
            <p>
              {language === 'fr'
                ? 'Votre partenaire de confiance pour les services de garantie prolongée automobile.'
                : 'Your trusted partner for automotive extended warranty services.'
              }
            </p>
            <p>
              D-U-N-S® {language === 'fr' ? 'Numéro' : 'Number'}: 243341709
            </p>
            <p>
              {language === 'fr' ? 'Numéro d\'inscription au CRTC' : 'CRTC Registration Number'}: 4465013400
            </p>
          </CompanyInfo>

          <FooterSection>
            <h4>{language === 'fr' ? 'Services' : 'Services'}</h4>
            <ul>
              <li>{language === 'fr' ? 'Garanties prolongées' : 'Extended warranties'}</li>
              <li>{language === 'fr' ? 'Support concessionnaires' : 'Dealer support'}</li>
              <li>{language === 'fr' ? 'Gestion des réclamations' : 'Claims management'}</li>
              <li>{language === 'fr' ? 'Formation' : 'Training'}</li>
            </ul>
          </FooterSection>

          <FooterSection>
            <h4>{language === 'fr' ? 'Contact' : 'Contact'}</h4>
            <p>Email: <a href={`mailto:${t.contact.email}`}>{t.contact.email}</a></p>
            <p>{language === 'fr' ? 'Téléphone' : 'Phone'}: <a href={`tel:${t.contact.phone}`}>{t.contact.phone}</a></p>
          </FooterSection>
        </FooterTop>

        <FooterBottom>
          <p>
            © {currentYear} {language === 'fr' ? 'Groupe Garant' : 'Garant Group'}. {language === 'fr' ? 'Tous droits réservés.' : 'All rights reserved.'} | {' '}
            <a href="/privacy-policy" style={{ color: 'inherit', textDecoration: 'none', borderBottom: '1px solid transparent', transition: 'all 0.3s ease' }}
              onMouseEnter={(e) => { e.target.style.color = '#b7ab9f'; e.target.style.borderBottomColor = '#b7ab9f'; }}
              onMouseLeave={(e) => { e.target.style.color = 'inherit'; e.target.style.borderBottomColor = 'transparent'; }}>
              {language === 'fr' ? 'Politique de confidentialité' : 'Privacy Policy'}
            </a>
          </p>
        </FooterBottom>
      </FooterContent>
    </FooterContainer>
  );
};

export default Footer; 
